import React, { useCallback, useEffect, useState } from 'react';
import {
  Card,
  Col,
  DatePicker,
  Drawer,
  Progress,
  Row,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import {
  shipmentDashboardApi,
  type DashboardMetricsDTO,
  type DashboardShipmentRowDTO,
  type DashboardStatusSection,
} from '../api/kfc';

const { RangePicker } = DatePicker;
const { Text, Link } = Typography;

type DrillState = {
  status: string;
  statusLabel: string;
  bucketLabel: string;
  bucket?: number;
  openBucket?: boolean;
};

const fmtDays = (v: number | null | undefined) =>
  v == null || Number.isNaN(v) ? '-' : `${v}`;

const ShipmentDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState<DashboardMetricsDTO | null>(null);
  const [sections, setSections] = useState<DashboardStatusSection[]>([]);
  const [loading, setLoading] = useState(false);
  const [range, setRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().subtract(90, 'day'),
    dayjs(),
  ]);
  const [drill, setDrill] = useState<DrillState | null>(null);
  const [drillRows, setDrillRows] = useState<DashboardShipmentRowDTO[]>([]);
  const [drillTotal, setDrillTotal] = useState(0);
  const [drillPage, setDrillPage] = useState(0);
  const [drillLoading, setDrillLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, d] = await Promise.all([
        shipmentDashboardApi.metrics({
          from: range[0].format('YYYY-MM-DD'),
          to: range[1].format('YYYY-MM-DD'),
        }),
        shipmentDashboardApi.dwell(),
      ]);
      setMetrics(m.data);
      setSections(d.data?.sections || []);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      message.error(err.response?.data?.message || '加载看板失败');
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    load();
  }, [load]);

  const loadDrill = useCallback(
    async (state: DrillState, page: number) => {
      setDrillLoading(true);
      try {
        const res = await shipmentDashboardApi.shipments({
          status: state.status,
          bucket: state.openBucket ? undefined : state.bucket,
          openBucket: state.openBucket || false,
          page,
          size: 20,
        });
        setDrillRows(res.data.content || []);
        setDrillTotal(res.data.totalElements || 0);
        setDrillPage(page);
      } catch (e: unknown) {
        const err = e as { response?: { data?: { message?: string } } };
        message.error(err.response?.data?.message || '加载下钻列表失败');
        setDrillRows([]);
      } finally {
        setDrillLoading(false);
      }
    },
    []
  );

  const openBucket = (sec: DashboardStatusSection, label: string, upperBound?: number | null) => {
    const state: DrillState = {
      status: sec.status,
      statusLabel: sec.statusLabel,
      bucketLabel: label,
      bucket: upperBound ?? undefined,
      openBucket: upperBound == null,
    };
    setDrill(state);
    loadDrill(state, 0);
  };

  const drillColumns: ColumnsType<DashboardShipmentRowDTO> = [
    {
      title: '运单号',
      dataIndex: 'shipmentId',
      width: 160,
      render: (id: string) => (
        <Link onClick={() => navigate(`/shipments/list?shipmentId=${encodeURIComponent(id)}`)}>{id}</Link>
      ),
    },
    { title: '客户', dataIndex: 'clientCode', width: 100 },
    { title: '代理', dataIndex: 'agentIdentifier', width: 100, render: (v) => v || '-' },
    {
      title: '状态变更时间',
      dataIndex: 'statusChangedAt',
      width: 170,
      render: (v?: string) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '-'),
    },
    {
      title: '停留天',
      dataIndex: 'dwellDays',
      width: 80,
      render: (v: number) => fmtDays(v),
    },
    {
      title: '告警',
      dataIndex: 'alert',
      width: 70,
      render: (a: boolean) => (a ? <Tag color="error">是</Tag> : <Tag>否</Tag>),
    },
  ];

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card
        title="运单看板"
        extra={
          <Space>
            <Text type="secondary">交付样本签收日</Text>
            <RangePicker
              value={range}
              onChange={(v) => {
                if (v?.[0] && v?.[1]) setRange([v[0], v[1]]);
              }}
              allowClear={false}
            />
          </Space>
        }
        loading={loading}
      >
        <Row gutter={16}>
          <Col xs={12} md={6}>
            <Statistic title="平均交付时长（天）" value={fmtDays(metrics?.avgDays)} />
          </Col>
          <Col xs={12} md={6}>
            <Statistic title="最大交付时长（天）" value={fmtDays(metrics?.maxDays)} />
          </Col>
          <Col xs={12} md={6}>
            <Statistic title="最小交付时长（天）" value={fmtDays(metrics?.minDays)} />
          </Col>
          <Col xs={12} md={6}>
            <Statistic title="P90 交付时长（天）" value={fmtDays(metrics?.p90Days)} />
          </Col>
        </Row>
        <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
          样本数：{metrics?.sampleCount ?? 0}（已签收且签收日落在所选区间）
        </Text>
      </Card>

      {sections.map((sec) => {
        const maxBucket = Math.max(1, ...sec.buckets.map((b) => b.count));
        return (
          <Card
            key={sec.status}
            title={
              <Space>
                <span>{sec.statusLabel}</span>
                <Tag>共 {sec.totalCount}</Tag>
                {sec.alertCount > 0 ? (
                  <Tag color="error">告警 {sec.alertCount}</Tag>
                ) : (
                  <Tag>告警 0</Tag>
                )}
                {sec.thresholdDays != null && (
                  <Text type="secondary">阈值 {sec.thresholdDays} 天</Text>
                )}
              </Space>
            }
            size="small"
          >
            <Row gutter={[16, 8]}>
              {sec.buckets.map((b) => (
                <Col key={b.label} xs={24} sm={12}>
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}
                    onClick={() => openBucket(sec, b.label, b.upperBound)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') openBucket(sec, b.label, b.upperBound);
                    }}
                  >
                    <Text style={{ width: 48, flexShrink: 0 }}>{b.label}</Text>
                    <Progress
                      percent={Math.round((b.count / maxBucket) * 100)}
                      showInfo={false}
                      style={{ flex: 1, margin: 0 }}
                      size="small"
                    />
                    <Text style={{ width: 40, textAlign: 'right', flexShrink: 0 }}>{b.count}</Text>
                  </div>
                </Col>
              ))}
            </Row>
          </Card>
        );
      })}

      <Drawer
        title={
          drill
            ? `${drill.statusLabel} · ${drill.bucketLabel}`
            : '下钻'
        }
        open={!!drill}
        onClose={() => setDrill(null)}
        width={720}
      >
        <Table
          loading={drillLoading}
          rowKey="shipmentId"
          dataSource={drillRows}
          columns={drillColumns}
          pagination={{
            current: drillPage + 1,
            pageSize: 20,
            total: drillTotal,
            onChange: (p) => drill && loadDrill(drill, p - 1),
          }}
          size="small"
        />
      </Drawer>
    </Space>
  );
};

export default ShipmentDashboard;
